/**
 * Phase 302 — KioskMode (Front-Desk iPad Entry)
 *
 * Designed to sit OPEN on the academy's front-desk iPad. A new parent
 * walks up, taps in their 10-digit phone number on a giant numpad, and
 * the system fires a WhatsApp magic link to their phone. They walk away
 * holding their phone, complete onboarding in the conversational bot.
 *
 * Auth: protected — staff role required. The page itself is full-screen
 * (no AppShell wrapper) so the iPad feels like a kiosk app.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, X, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';

const NUMPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['⌫', '0', 'C'],
];

export default function KioskMode() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [sentLast4, setSentLast4] = useState(null); // null | "3210"
  const [academyName, setAcademyName] = useState('');

  const handleKey = useCallback((k) => {
    if (k === '⌫') { setPhone((p) => p.slice(0, -1)); return; }
    if (k === 'C') { setPhone(''); return; }
    setPhone((p) => (p.length >= 10 ? p : p + k));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (phone.length !== 10) { toast.error('Please enter a 10-digit number'); return; }
    setSending(true);
    try {
      const r = await api.post('/intake/kiosk-dispatch', { phone_number: phone });
      setSentLast4(r.data?.phone_suffix_last4 || phone.slice(-4));
      setAcademyName(r.data?.academy_name || '');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || detail?.code || 'Could not send the link — try again');
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }, [phone]);

  const handleReset = useCallback(() => {
    setSentLast4(null);
    setAcademyName('');
    setPhone('');
  }, []);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex flex-col"
      data-testid="kiosk-mode-page"
    >
      {/* Subtle header so staff can exit kiosk mode */}
      <header className="px-6 py-3 flex items-center justify-between">
        <p className="text-xs font-semibold tracking-widest text-teal-700">OUT-PLAY · KIOSK MODE</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="text-xs text-slate-500"
          data-testid="kiosk-exit-btn"
        >
          Exit
        </Button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 max-w-2xl w-full mx-auto pb-12">
        {sentLast4 ? (
          /* ── SUCCESS state ── */
          <div className="text-center" data-testid="kiosk-success-state">
            <div className="mx-auto w-28 h-28 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-16 w-16 text-emerald-600" strokeWidth={2.5} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-emerald-700 mb-3" data-testid="kiosk-success-headline">
              Link sent!
            </h1>
            <p className="text-lg text-slate-700 max-w-md mx-auto leading-relaxed">
              Please check your WhatsApp on the number ending in <span className="font-mono font-bold">{sentLast4}</span> to finish your registration.
            </p>
            {academyName && (
              <p className="text-sm text-slate-500 mt-3">Welcome to {academyName}!</p>
            )}
            <Button
              size="lg"
              onClick={handleReset}
              className="mt-10 h-14 px-8 text-lg bg-teal-600 hover:bg-teal-700"
              data-testid="kiosk-reset-btn"
            >
              Next Parent
            </Button>
          </div>
        ) : (
          /* ── PHONE-ENTRY state ── */
          <div className="w-full" data-testid="kiosk-entry-state">
            <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900 text-center mb-1">
              Welcome to Out-Play
            </h1>
            <p className="text-base sm:text-lg text-slate-600 text-center mb-8 max-w-lg mx-auto">
              Enter your phone number to sign the waiver and complete registration.
            </p>

            {/* Phone display */}
            <div
              className="mx-auto max-w-sm bg-white rounded-2xl shadow-sm border-2 border-slate-200 p-5 mb-6"
              data-testid="kiosk-phone-display"
            >
              <div className="flex items-center justify-center gap-3">
                <Phone className="h-6 w-6 text-slate-400" />
                <p className="text-3xl sm:text-4xl font-mono font-bold tracking-wider text-slate-900 tabular-nums">
                  {phone.padEnd(10, '·').split('').map((ch, i) => (
                    <span key={i} className={ch === '·' ? 'text-slate-300' : ''}>{ch}</span>
                  ))}
                </p>
              </div>
              <p className="text-xs text-center text-slate-400 mt-2">10-digit Indian mobile number</p>
            </div>

            {/* Numpad */}
            <div
              className="mx-auto max-w-sm grid grid-cols-3 gap-3"
              data-testid="kiosk-numpad"
            >
              {NUMPAD_KEYS.flat().map((k, i) => {
                const isAction = k === '⌫' || k === 'C';
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleKey(k)}
                    disabled={sending}
                    className={`h-16 sm:h-20 rounded-2xl text-2xl sm:text-3xl font-display font-bold transition-all active:scale-95 disabled:opacity-50
                      ${isAction
                        ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        : 'bg-white text-slate-900 hover:bg-teal-50 border border-slate-200'}
                    `}
                    data-testid={`kiosk-key-${k}`}
                  >
                    {k}
                  </button>
                );
              })}
            </div>

            {/* Submit button */}
            <div className="mx-auto max-w-sm mt-6">
              <Button
                size="lg"
                onClick={handleSubmit}
                disabled={sending || phone.length !== 10}
                className="w-full h-16 text-lg font-semibold bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50"
                data-testid="kiosk-send-btn"
              >
                {sending
                  ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Sending…</>
                  : <><Send className="h-5 w-5 mr-2" /> Send to Phone</>}
              </Button>
            </div>

            <p className="text-xs text-center text-slate-400 mt-5">
              We&apos;ll send a one-time WhatsApp link to finish registration.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
